import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import type {SliceComponentProps} from '@prismicio/react'
import {SliceZone} from '@prismicio/react'
import cn from 'classnames'
import {useScroll} from 'framer-motion'
import type {ReactNode} from 'react'
import {memo, useEffect, useMemo, useRef, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useMeasure} from 'react-use'
import {components} from 'slices'
import {Container} from 'src/common/Container'
import {useGetPricingFormHandler} from 'src/common/hooks/useGetPricingFormHandler/useGetPricingFormHandler'
import {Icon} from 'src/common/Icon'
import {Tag} from 'src/common/Tag'
import type {Context} from 'src/common/types'
import {FormSummary} from './FormSummary'
import {createInitialFormState, getRegistrationName, mapFormDataSections} from './helpers'

/**
 * Props for `FormSection`.
 */
export type FormSectionProps = SliceComponentProps<Content.FormSectionSlice> & {context: Context}

/**
 * Component for "FormSection" Slices.
 */
const FormSection = ({slice, context: {form_data, pageUid}, index: componentIndex}: FormSectionProps): JSX.Element => {
  const sections = useMemo(() => mapFormDataSections(form_data?.data), [form_data?.data])
  const successUrl = isFilled.link(slice.primary.success_url) ? slice.primary.success_url.url : ''
  const {pricingFormHandler} = useGetPricingFormHandler({successUrl})
  const formRef = useRef<HTMLFormElement>(null)
  const initialFormState = createInitialFormState(sections)
  const methods = useForm({defaultValues: initialFormState})
  const [shouldFixSummary, setShouldFixSummary] = useState(false)
  const summaryPositionRef = useRef<HTMLDivElement>(null)
  const {handleSubmit} = methods
  const onSubmit = handleSubmit(pricingFormHandler)
  const [summaryRef, {height: summaryRefHeight}] = useMeasure<HTMLDivElement>()
  const [shouldExpand, setShouldExpand] = useState(false)
  const isReskinEnabled = slice.primary.enable_reskin

  const {
    formState: {errors: formErrors, isSubmitted},
  } = methods

  const {scrollYProgress} = useScroll({
    target: formRef,
    offset: ['-.2 start', 'end .55'],
  })

  // Set fixed summary state
  useEffect(() => {
    const handleSummary = (latest: number) => {
      const fixedNumber = latest

      if ((fixedNumber < 0.06 && shouldFixSummary) || (fixedNumber > 0.95 && shouldFixSummary)) {
        setShouldFixSummary(false)
      }

      if (fixedNumber > 0.06 && fixedNumber < 0.95 && !shouldFixSummary) {
        setShouldFixSummary(true)
      }
    }

    scrollYProgress.on('change', handleSummary)

    return () => {
      scrollYProgress.destroy()
    }
  }, [scrollYProgress, shouldFixSummary])

  const observableRef = useRef<IntersectionObserver | null>(null)

  // Create a correct observer according to summary height
  useEffect(() => {
    const intersectionRatio = Number((summaryRefHeight / summaryPositionRef.current?.clientHeight!).toFixed(2))

    if (
      (intersectionRatio > 0.5 && intersectionRatio < 0.65) ||
      (intersectionRatio > 0.75 && intersectionRatio < 0.99)
    ) {
      observableRef.current?.disconnect()
      observableRef.current = null

      observableRef.current = new IntersectionObserver(
        (entries) => {
          const summaryEntry = entries[0]

          if (summaryEntry.isIntersecting) {
            setShouldFixSummary(false)
            setShouldExpand(false)
          }
        },
        {
          threshold: intersectionRatio + 0.1,
        },
      )

      observableRef.current.observe(summaryPositionRef.current!)
    }

    return () => observableRef.current?.disconnect()
  }, [summaryRefHeight])

  return (
    <Container
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      backgroundColor="Moss White"
      className="md:min-h-[858px] md:overflow-visible lg:min-h-[798px]"
      id="form-section"
    >
      <FormProvider {...methods}>
        <div
          className={cn('grid-base max-w-[1200px] md:grid md:grid-cols-10 md:gap-0 md:px-10 lg:px-5 xl:px-0', {
            'bg-beige-100 md:rounded-l-md px-5': !isReskinEnabled,
            'bg-moss-white md:rounded-l-lg px-0': isReskinEnabled,
          })}
        >
          <form
            className={cn('col-span-4 grid h-min gap-y-6 md:col-span-7', {
              'md:px-[30px] md:py-8 md:pr-[60px] bg-beige-100 md:rounded-l-md pt-10 lg:gap-y-5': !isReskinEnabled,
              'py-8 px-5 md:px-8 bg-moss-grey-100 md:rounded-lg md:mr-3 lg:gap-10': isReskinEnabled,
            })}
            onSubmit={onSubmit}
            ref={formRef}
          >
            {isFilled.keyText(slice.primary.title) && (
              <div className="flex items-center justify-center gap-2.5 pb-1.5 lg:pb-2.5">
                <p className="text-h3 tracking-[-.32px] text-moss-black">{slice.primary.title}</p>
                {isFilled.keyText(slice.primary.tag) && <Tag color="green" label={slice.primary.tag} />}
              </div>
            )}
            {sections?.map((section, index) => {
              if (!section.slices) {
                return null
              }

              const sectionRegistrationName = getRegistrationName(section.title)

              const hasError = sectionRegistrationName in formErrors && isSubmitted

              return (
                <div className="flex flex-col gap-3" key={index}>
                  <fieldset className="col-span-full flex flex-col gap-3">
                    <p
                      className={cn('uppercase text-grey-550', {
                        'text-button-s': isReskinEnabled,
                        'text-body-xs': !isReskinEnabled,
                      })}
                    >
                      {section.title as ReactNode}
                    </p>
                    <div
                      className={cn('flex flex-col divide-grey-150', {
                        'lg:grid lg:grid-cols-12 lg:rounded-lg lg:divide-y-0': section.isGrid,
                        'divide-y lg:divide-x': section.isGrid && !isReskinEnabled,
                        'divide-y': !section.isGrid,
                        'outline outline-1 outline-ui-red-300': hasError && !isReskinEnabled,
                        'gap-3 divide-y-0': isReskinEnabled,
                        'gap-0': !isReskinEnabled,
                      })}
                    >
                      <SliceZone
                        slices={section.slices}
                        components={components}
                        context={{
                          parentFormType: section.type,
                          pageUid: pageUid,
                          isGrid: section.isGrid,
                          sectionLength: section.slices.length,
                          fieldName: sectionRegistrationName,
                          isSectionRequired: section.required,
                          errorMessage: section.errorMessage,
                          enableReskin: isReskinEnabled,
                          hasError: hasError,
                        }}
                      />
                    </div>
                  </fieldset>
                  {hasError && section.errorMessage && (
                    <div className="flex items-center gap-1">
                      <div className="flex size-2.5 items-center justify-center rounded-[600px] bg-ui-red-450">
                        <Icon name="cross-bold" className="size-1.5 text-moss-white" />
                      </div>
                      <span className="text-[12px] leading-4 tracking-[.12px] text-ui-red-450">
                        {section.errorMessage}
                      </span>
                    </div>
                  )}
                </div>
              )
            })}
          </form>
          <div
            className={cn(
              'relative col-span-4 flex flex-col gap-6 rounded-xl bg-transparent md:col-span-3 md:h-full md:min-h-full',
              {
                'h-[475px]': shouldFixSummary,
                'h-min': !shouldFixSummary,
              },
            )}
            ref={summaryPositionRef}
          >
            <FormSummary
              sections={sections!}
              summaryTitle={form_data?.data.summary_title}
              tag={form_data?.data.summary_tag}
              bottomTitle={form_data?.data.summary_bottom_section_title}
              bottomText={form_data?.data.summary_bottom_text}
              submitLabel={form_data?.data.summary_submit_button}
              shouldBeFixed={shouldFixSummary}
              summaryRef={summaryRef}
              formRef={formRef}
              shouldExpand={shouldExpand}
              setShouldExpand={setShouldExpand}
              parentIndex={componentIndex}
              pageUid={pageUid}
              description={form_data?.data.summary_description}
              enableReskin={slice.primary.enable_reskin}
            />
          </div>
        </div>
      </FormProvider>
    </Container>
  )
}

export default memo(FormSection)
